import { gql } from '@apollo/client';
import { FileType } from '@jbc-year-end-adj/2024/types';

export type QueryResult = {
  request: {
    id: string;
    profile: Profile;
  };
};

export type Attachment = {
  id: string;
  file: FileType;
};

export type Profile = {
  id: string;
  attachments: Attachment[];
  formerJobs: {
    id: string;
    withholdingSlipImage: FileType;
  }[];
  workingStudentClassification: {
    id: string;
    image: FileType;
  };
  handicapClassification: {
    id: string;
    image: FileType;
  };
  generalLifeInsurances: {
    id: string;
    image: FileType;
  }[];
  nursingMedicalInsurances: {
    id: string;
    image: FileType;
  }[];
  individualAnnuityInsurances: {
    id: string;
    image: FileType;
  }[];
  earthquakeInsurances: {
    id: string;
    image: FileType;
  }[];
  socialInsurances: {
    id: string;
    image: FileType;
  }[];
  premium: {
    id: string;
    smallAndMediumSizedEnterprisesImage: FileType;
    personalPensionImage: FileType;
    corporatePensionImage: FileType;
    disabilityMutualAidImage: FileType;
  };
  housingLoan: {
    id: string;
    image: FileType;
    certificateImage: FileType;
  };
  dependents: {
    id: string;
    type: 'Spouse' | 'OtherFamily';
    thisYear: {
      id: string;
      handicapImage: FileType;
      relatedToRelativesDocument: FileType;
      relatedToRemittanceDocument: FileType;
      provingStudyAbroadDocument: FileType;
    };
    nextYear: {
      id: string;
      handicapImage: FileType;
      relatedToRelativesDocument: FileType;
      relatedToRemittanceDocument: FileType;
      provingStudyAbroadDocument: FileType;
    };
  }[];
};

export const ATTACHMENTS_FRAGMENT = gql`
  fragment Attachments on Profile {
    attachments {
      id
      file {
        url
        filename
      }
    }
  }
`;

export const ATTACHED_FILES_FRAGMENT = gql`
  fragment AttachedFiles on Profile {
    formerJobs {
      id
      withholdingSlipImage {
        ...AttachedFile
      }
    }
    workingStudentClassification {
      id
      image {
        ...AttachedFile
      }
    }
    handicapClassification {
      id
      image {
        ...AttachedFile
      }
    }
    generalLifeInsurances {
      id
      image {
        ...AttachedFile
      }
    }
    nursingMedicalInsurances {
      id
      image {
        ...AttachedFile
      }
    }
    individualAnnuityInsurances {
      id
      image {
        ...AttachedFile
      }
    }
    earthquakeInsurances {
      id
      image {
        ...AttachedFile
      }
    }
    socialInsurances {
      id
      image {
        ...AttachedFile
      }
    }
    premium {
      id
      smallAndMediumSizedEnterprisesImage {
        ...AttachedFile
      }
      personalPensionImage {
        ...AttachedFile
      }
      corporatePensionImage {
        ...AttachedFile
      }
      disabilityMutualAidImage {
        ...AttachedFile
      }
    }
    housingLoan {
      id
      image {
        ...AttachedFile
      }
      certificateImage {
        ...AttachedFile
      }
    }
    dependents {
      id
      type
      thisYear {
        id
        handicapImage {
          ...AttachedFile
        }
        relatedToRelativesDocument {
          ...AttachedFile
        }
        relatedToRemittanceDocument {
          ...AttachedFile
        }
        provingStudyAbroadDocument {
          ...AttachedFile
        }
      }
      nextYear {
        id
        handicapImage {
          ...AttachedFile
        }
        relatedToRelativesDocument {
          ...AttachedFile
        }
        relatedToRemittanceDocument {
          ...AttachedFile
        }
        provingStudyAbroadDocument {
          ...AttachedFile
        }
      }
    }
  }

  fragment AttachedFile on FileObject {
    url
    filename
  }
`;

export const FETCH_ATTACHMENTS = gql`
  query attachments {
    request {
      id
      profile {
        id
        ...Attachments
        ...AttachedFiles
      }
    }
  }
  ${ATTACHMENTS_FRAGMENT}
  ${ATTACHED_FILES_FRAGMENT}
`;
